import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { IDatePickerConfig } from 'ng2-date-picker';
import { getAvailableTransitAllowance, getAvailableUnpaidBreak } from 'src/modules/company-dashboard/constants/job-constants';
import { BroadcastJobsUtilService } from 'src/modules/company-dashboard/services/broadcast-job-utils.service';
import { RecurrentShiftService } from 'src/modules/talent-dashboard/services/recurrentShift.service';
import { IShiftTime } from '../../interfaces/shift-time.interface';
import { FormCheckboxComponent } from '../form-checkbox/form-checkbox.component';
import { CompanyTypeEnum } from 'src/modules/onboarding-company/enums/company-type.enum';
import { UnpaidBreak } from '../../enums/unpaid-break.enum';


@Component({
  selector: 'app-edit-recurring-shift-list-item',
  templateUrl: './edit-recurring-shift-list-item.component.html',
  styleUrls: ['./edit-recurring-shift-list-item.component.scss'],
})

export class EditRecurringShiftListItemComponent implements OnInit {
  @ViewChild('checkBox', { static: false }) checkBoxRef: FormCheckboxComponent;
  @Input() config: Array<string>;
  @Input() control: any;
  @Input() data;
  @Input() showEditIcon? : boolean = true
  @Input() confirmationView: Boolean = false
  @Input() companyType?: string;
  @Input() settings?: any;
  @Input() currentView?: string;

  /**
   * Extra cssClass for the dropdown popups in these rows, plus a heading taken
   * from the column name. The create-shift page passes 'staffy-select'.
   */
  @Input() interfaceCssClass: string = '';

  // Opt-in: renders the add/remove controls that let one date carry several shifts.
  @Input() showSlotActions?: boolean = false;
  @Input() canRemoveSlot?: boolean = false;
  @Input() canAddSlot?: boolean = true;
  // A date prints itself once, on the first of the rows it owns.
  @Input() showDate?: boolean = true;
  availableTransitAllowance: Array<{label: string, value: string}>;
  availableUnpaidBreak: Array<{label: string, value: string}>;
  startTimeValues : Array<IShiftTime>;
  endTimeValues : Array<IShiftTime>;
  // The full option lists. startTimeValues and endTimeValues are these narrowed
  // down to the times that can still pair with the other half of the shift.
  allStartTimeValues : Array<IShiftTime> = [];
  allEndTimeValues : Array<IShiftTime> = [];
  @Output() onToggleCheckBox = new EventEmitter<boolean>();
  @Output() addSlot = new EventEmitter<void>();
  @Output() removeSlot = new EventEmitter<void>();
  isDelete: boolean = false;
  checkBoxOptions = [{
    label: ""
  }]

  dateConfig: IDatePickerConfig = {
    allowMultiSelect: false,
    format: 'DD MMM,YY',
    closeOnSelect: true,
    showGoToCurrent: true,
    disableKeypress: true,
    unSelectOnClick: false,
    hideOnOutsideClick: true
  };

  constructor(private cdr: ChangeDetectorRef, private broadcastJobsUtilService: BroadcastJobsUtilService, private recurrentShiftService: RecurrentShiftService) { }

  ngOnInit() {

    if (this.confirmationView) {
      return
    }
    this.availableTransitAllowance = getAvailableTransitAllowance()

    this.availableUnpaidBreak = getAvailableUnpaidBreak()

    this.getStartandEndTimeForDay(
      !this.showEditIcon ?  moment(this.control.get("date").value, 'MMM DD, YYYY ddd').format() : moment(moment().format('MM/DD/YYYY'), 'MM/DD/YYYY').format(),
      !this.showEditIcon ?  moment(this.control.get("date").value, 'MMM DD, YYYY ddd').format() : moment(moment().format('MM/DD/YYYY'), 'MM/DD/YYYY').format(),
    );

    const startIndex = this.findTimingIndex(this.startTimeValues, 'startTime');
    const endIndex = this.findTimingIndex(this.endTimeValues, 'endTime');
    const unpaidBreakIndex = this.recurrentShiftService.fetchUnpaidBreakIndex(this.availableUnpaidBreak, this.control, 'unpaidBreak');
    const travelAllowanceIndex = this.recurrentShiftService.fetchTravelAllowanceIndex(this.availableTransitAllowance, this.control, 'transitAllowance');

    if (startIndex > -1) {
      this.control.get('startTime').setValue(this.startTimeValues[startIndex]);
    }

    if (endIndex > -1) {
      this.control.get('endTime').setValue(this.endTimeValues[endIndex]);
    }

    if (unpaidBreakIndex > -1) {
      this.control.get('unpaidBreak').setValue(this.availableUnpaidBreak[unpaidBreakIndex]);
    }

    if (travelAllowanceIndex > -1) {
      this.control.get('transitAllowance').setValue(this.availableTransitAllowance[travelAllowanceIndex]);
    }

    // Narrowed last. The times arrive anchored to whichever date the shared
    // pickers were on, and the two setValue calls above are what re-anchors them
    // onto this row's own date. Narrowing before that would measure them against
    // a different day.
    this.refreshStartTimeOptions();
    this.refreshEndTimeOptions();

  }

  /**
   * Finds the dropdown option holding this control's time.
   *
   * Matches on the timestamp first: the option list carries both a same-day and a
   * next-day entry for every early-morning time, and they share a "01:00 AM"
   * label, so matching on the label alone snaps an overnight end time back to the
   * same day. Falls back to the label for the edit views, whose options are built
   * for today while the value belongs to the shift's own date — there the
   * timestamps never line up and the label is all there is to go on.
   */
  private findTimingIndex(options: Array<IShiftTime>, key: string): number {
    const value = this.control.get(key)?.value;

    // A shift added onto a day that already has one arrives with no times picked.
    if (!value) {
      return -1;
    }

    const indexByTimestamp = options.findIndex(option => option.value === value.value);

    if (indexByTimestamp > -1) {
      return indexByTimestamp;
    }

    return this.recurrentShiftService.fetchTimingIndex(options, this.control, key);
  }

  getStartandEndTimeForDay(startVal: string, endVal: string) {
    this.allStartTimeValues = this.broadcastJobsUtilService.generatedStepArray(startVal);
    this.allEndTimeValues = this.broadcastJobsUtilService.generatedStepArray(endVal);
    this.startTimeValues = this.allStartTimeValues;
    this.endTimeValues = this.allEndTimeValues;
  }

  /**
   * Narrows each dropdown to the times that can still pair with the other half of
   * the shift: a start has to land before the picked end, an end after the picked
   * start. The admin panel puts the same pair of filters on its shift slots, so a
   * slot can never be picked back to front in the first place.
   *
   * Only the create-shift table is narrowed. The edit views build their options
   * for today while the value being edited belongs to the shift's own date, so
   * comparing those two timestamps would empty the list.
   */
  private refreshStartTimeOptions() {
    const endTime = this.showEditIcon ? null : this.control.get('endTime')?.value;

    this.startTimeValues = endTime
      ? this.narrowedOr(this.allStartTimeValues, time => time.value < endTime.value)
      : this.allStartTimeValues;
  }

  private refreshEndTimeOptions() {
    const startTime = this.showEditIcon ? null : this.control.get('startTime')?.value;

    this.endTimeValues = startTime
      ? this.narrowedOr(this.allEndTimeValues, time => time.value > startTime.value)
      : this.allEndTimeValues;
  }

  /**
   * Narrows a list of times, unless doing so would leave nothing to pick from.
   *
   * The other half of the pair can still be sitting on a date this list knows
   * nothing about - times seeded from the shared pickers belong to whichever date
   * those were on until the row re-anchors them - and every option would then
   * measure as out of range. An empty dropdown is worse than an unnarrowed one.
   */
  private narrowedOr(options: Array<IShiftTime>, keep: (time: IShiftTime) => boolean): Array<IShiftTime> {
    const isMarker = (time: IShiftTime) => time?.value === undefined || time?.value === null;
    const narrowed = options.filter(time => isMarker(time) || keep(time));

    return narrowed.some(time => !isMarker(time)) ? narrowed : options;
  }

  getAvailableOptions(key: string) {
    if (key == "Start Time") {
      return this.startTimeValues
    } else if (key == "End Time") {
      return this.endTimeValues
    } else if (key == "Transit Allowance") {
      return this.availableTransitAllowance
    } else if (key == "Unpaid Break") {
      return this.availableUnpaidBreak
    }
  }

  getFormControl(key: string) {
    if (key == "Date") {
      return 'date'
    } else if (key == "Start Time") {
      return 'startTime'
    } else if (key == "End Time") {
      return 'endTime'
    } else if (key == "Transit Allowance") {
      return 'transitAllowance'
    } else if (key == "Unpaid Break") {
      return 'unpaidBreak'
    } else if (key == "selected") {
      return 'selected'
    }
  }

  getFormControlValue(key: string) {
    if (!this.control.get(this.getFormControl(key))) {
      return false;
    }

    // A shift added onto a day that already has one carries no times until they
    // are picked, and `typeof null` is also "object".
    if (this.control.get(this.getFormControl(key)).value == null) {
      return '';
    }

    if (typeof this.control.get(this.getFormControl(key)).value == "object") {
      return this.control.get(this.getFormControl(key)).value.formatted || this.control.get(this.getFormControl(key)).value.label
    }
    return this.control.get(this.getFormControl(key)).value;
  }

  getLabelKey(key: string) {
    if (key == "Start Time" || key == "End Time") {
      return 'formatted'
    } else {
      return 'label'
    }
  }

  onValueChange(event: any, key: string) {
    // update break time when start time or end time is changed
    if (!this.showEditIcon && ( key === 'Start Time' || key === 'End Time')) {
      
      if(key === 'Start Time') {
        this.populateEndTimeList();
      }

      // The other dropdown now has a new partner to sit against.
      if(key === 'End Time') {
        this.refreshStartTimeOptions();
      }

      if(!this.control.get('unpaidBreak')){
        return;
      }

      // Both halves are needed to size the break; a freshly added shift may only
      // have one of them picked so far.
      if(!this.control.get('startTime').value || !this.control.get('endTime').value){
        return;
      }

      const unpaidBreak = this.setUnpaidBreakDropdown(this.control.get('startTime').value, this.control.get('endTime').value);
      this.control.get('unpaidBreak').setValue({
        label: unpaidBreak,
        value: unpaidBreak,
      });
      const unpaidBreakIndex = this.recurrentShiftService.fetchUnpaidBreakIndex(this.availableUnpaidBreak,this.control,'unpaidBreak');
      if(unpaidBreakIndex > -1) {
        this.control.get('unpaidBreak').setValue(this.availableUnpaidBreak[unpaidBreakIndex]);
      }
    }
  } 

  populateEndTimeList() {

    const startTimeObj = this.control.get('startTime').value;
    const startTimeMoment = moment.unix(startTimeObj.value); // Assuming `singleDayStartTime.value` is a Unix timestamp
    const minEndTime = startTimeMoment.clone().add(15, 'minutes').format(); // 15 minutes ahead

    // Generate possible end times and filter invalid ones
    this.allEndTimeValues = this.broadcastJobsUtilService.generatedStepArray(minEndTime);
    this.endTimeValues = this.allEndTimeValues
    .filter((time) => moment.unix(Number(time.value)).isAfter(startTimeMoment) || time.formatted === 'Next Day'); // Filter values after start time

    // An end time that still sits after the new start is left alone, the way the
    // admin panel leaves the other half of the pair alone. Only an end that is
    // missing, or no longer possible, falls back to the earliest one on offer.
    const currentEndTime = this.control.get('endTime').value;
    if (currentEndTime && moment.unix(currentEndTime.value).isAfter(startTimeMoment)) {
      return;
    }

    this.control.get('endTime').setValue(this.endTimeValues[0]);
  }


  setUnpaidBreakDropdown(startTime, endTime) {
    if(this.companyType == CompanyTypeEnum.HEALTHCARE || this.companyType == CompanyTypeEnum.RETIREMENT_HOME){
      const diffInMilliseconds = moment(endTime.value * 1000).diff(startTime.value * 1000);
      const diffInMinutes = Math.round(moment.duration(diffInMilliseconds).asMinutes());
  
      let unpaidBreak: UnpaidBreak;
      if (this.settings) {
        for (const unpaidBreakConfig of this.settings[0].unpaidBreakConfigs) {
          if (diffInMinutes >= unpaidBreakConfig.shiftTimeInMinutes) {
            unpaidBreak = unpaidBreakConfig.breakTime;
          }
        }
      }
      return unpaidBreak;
    } else{
      return UnpaidBreak.EMPTY;
    }
   
  };
  toggleCheckBox() {
    let value = this.getFormControlValue("selected")
    this.onToggleCheckBox.emit(!value);
    this.control.get("selected").setValue(!value)
  }
}

